import type {
  EditalDto,
  RespostaPerguntaDto,
  AceitarConviteEntrada,
  ConviteDto,
  SolicitacaoPreenchidaDto,
  SolicitarAcessoEntrada,
  AdicionarPessoaEntrada,
  AlterarAtividadeEntrada,
  AlterarGruposEntrada,
  AlterarLayoutEntrada,
  ArrasteRemoto,
  CriarAtividadeEntrada,
  EditarProcessoEntrada,
  EntrarQuadroResposta,
  EsvaziarLixeiraEntrada,
  EnvioDocumentoDto,
  LicitacaoDto,
  NotificacaoDto,
  NotificacoesDto,
  PessoaResumoDto,
  UrlArquivoDto,
  AvisoMudancaEstado,
  EuDto,
  EventoQuadroDto,
  ItemArrastado,
  MoverProcessoEntrada,
  MudarEstadoEntrada,
  QuadroDto,
  RestaurarProcessoEntrada,
  TravaDto,
  TravarResposta,
} from '@nexital/shared';
import type { Camera } from '../domain/camera';

/** Versão nova do processo e, quando a ação fez grupos crescerem, as versões novas deles. */
export type ProcessoGravado = { versao: number; versoesGrupos?: Record<string, number> };

/** Falha de uma chamada à API, com o código HTTP (409 = alterado por outra pessoa; 422 = avisos a confirmar). */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    readonly avisos: AvisoMudancaEstado[] = [],
  ) {
    super(mensagem);
  }
}

export interface ApiQuadro {
  obterQuadro(quadroUuid: string): Promise<QuadroDto>;
  moverProcesso(quadroUuid: string, processoUuid: string, entrada: MoverProcessoEntrada): Promise<ProcessoGravado>;
  alterarGrupos(quadroUuid: string, entrada: AlterarGruposEntrada): Promise<{ versoes: Record<string, number> }>;
  mudarEstado(quadroUuid: string, processoUuid: string, entrada: MudarEstadoEntrada): Promise<ProcessoGravado>;
  restaurarProcesso(
    quadroUuid: string,
    processoUuid: string,
    entrada: RestaurarProcessoEntrada,
  ): Promise<{ versao: number; estadoUuid: string }>;
  esvaziarLixeira(quadroUuid: string, entrada: EsvaziarLixeiraEntrada): Promise<{ excluidos: number }>;
}

/** Área da licitação (RQ-LIC-001 a RQ-LIC-004) e documentos (RQ-ARQ-002, RQ-ARQ-006). Caminho: quadro + processo. */
export type AlvoLicitacao = { quadroUuid: string; processoUuid: string };

export interface ApiLicitacao {
  obterLicitacao(alvo: AlvoLicitacao): Promise<LicitacaoDto>;
  editarProcesso(alvo: AlvoLicitacao, entrada: EditarProcessoEntrada): Promise<{ versao: number }>;
  criarAtividade(alvo: AlvoLicitacao, entrada: CriarAtividadeEntrada): Promise<{ uuid: string; versao: number }>;
  alterarAtividade(alvo: AlvoLicitacao, atividadeUuid: string, entrada: AlterarAtividadeEntrada): Promise<{ versao: number }>;
  adicionarPessoa(alvo: AlvoLicitacao, entrada: AdicionarPessoaEntrada): Promise<{ uuid: string }>;
  removerPessoa(alvo: AlvoLicitacao, pessoaUuid: string): Promise<void>;
  alterarLayout(alvo: AlvoLicitacao, entrada: AlterarLayoutEntrada): Promise<{ versoes: Record<number, number> }>;
  enviarDocumento(alvo: AlvoLicitacao, arquivo: File): Promise<EnvioDocumentoDto>;
  urlDocumento(alvo: AlvoLicitacao, documentoUuid: string): Promise<UrlArquivoDto>;
  /** Edital lido pela IA (base comum); null quando a licitação ainda não tem edital. */
  obterEdital(alvo: AlvoLicitacao): Promise<EditalDto | null>;
  perguntar(alvo: AlvoLicitacao, pergunta: string): Promise<RespostaPerguntaDto>;
}

/** O que o canal de tempo real avisa à tela (ADR-0008). */
export type OuvintesTempoReal = {
  /** Entrou (ou voltou) na sala do quadro; na volta, traz o que foi perdido. */
  aoConectar(resposta: EntrarQuadroResposta): void;
  aoDesconectar(): void;
  /** Só eventos de outras abas (os da própria aba já estão na tela). */
  aoEvento(evento: EventoQuadroDto): void;
  aoPresenca(pessoas: PessoaResumoDto[]): void;
  aoArraste(arraste: ArrasteRemoto): void;
  aoTravas(travas: TravaDto[]): void;
  /** Aviso novo para a pessoa (sino), de qualquer quadro. */
  aoNotificacao(notificacao: NotificacaoDto): void;
};

/** Avisos da pessoa conectada (RQ-NTF-001). */
export interface ApiNotificacoes {
  obterNotificacoes(): Promise<NotificacoesDto>;
  marcarNotificacoesLidas(uuids?: string[]): Promise<void>;
}

/** Colaboração em tempo real: sala do quadro, arraste ao vivo e travas (RQ-COL-001 a RQ-COL-006). */
export interface CanalTempoReal {
  /** Id desta aba, enviado também nas gravações (cabeçalho x-nexital-cliente). */
  readonly clienteId: string;
  /** Conecta e entra na sala; devolve como sair. */
  entrar(quadroUuid: string, ouvintes: OuvintesTempoReal): () => void;
  arrastando(itens: ItemArrastado[]): void;
  travar(chave: string): Promise<TravarResposta>;
  liberar(chave: string): void;
}

/** Quem está conectado e seus espaços de trabalho (empresas e quadros). */
export interface ApiEspacos {
  obterEu(): Promise<EuDto>;
}

/** De onde o cliente HTTP tira o JWT e a quem pede renovação quando recebe 401. */
export interface FonteToken {
  readonly token: string | null;
  renovar(): Promise<boolean>;
  expirou(): void;
}

/** Preferências guardadas só neste dispositivo (RQ-QDR-007). */
export interface ArmazenamentoLocal {
  lerCamera(quadroUuid: string): Camera | null;
  salvarCamera(quadroUuid: string, camera: Camera): void;
}

export type Sessao = { usuarioUuid: string; quadroUuid: string; empresaUuid: string; nomeUsuario?: string };

/** Páginas públicas de acesso (RQ-ACE-003): link do convite e pedido de acesso de quem não tem convite. */
export interface ApiAcessoPublico {
  consultarConvite(token: string): Promise<ConviteDto>;
  aceitarConvite(token: string, dados: AceitarConviteEntrada): Promise<void>;
  solicitacaoPreenchida(token: string): Promise<SolicitacaoPreenchidaDto>;
  solicitarAcesso(dados: SolicitarAcessoEntrada): Promise<void>;
}
